// "지금 뜨는 스타일" 전체 화면 오버레이 (STYLE_GALLERY.md).
//
// 라우트가 아니라 오버레이인 이유: 스타일 선택 단계에서 다른 페이지로
// 이동하면 올려둔 사진(컴포넌트 상태)이 날아가 생성 워크플로우가 끊긴다.
//
// 랭킹은 usesCount(실사용자 수) 기준 — 혼자 반복 사용은 인기가 아니다.
// 기본 스타일은 썸네일 이미지 카드로 (텍스트 벽 금지), 커스텀은 전문 카드.
// 여기서 고르면 닫히면서 스타일 선택 화면의 해당 위치로 스크롤된다.
//
// 2.3.0 — 갤러리 탭(/gallery)에서도 같은 컴포넌트를 쓴다 (asPage). 탭에선
// 올려둔 사진이 없으니 라우트여도 되고, 고르면 /create 로 넘어간다 (Gallery.jsx).
import { useState, useEffect, useRef } from 'react';
import { useLocale } from '../hooks/useLocale.jsx';
import { rankUses, customAggregate, translateStyles } from '../services/style-stats-service.js';
import { db } from '../firebase.js';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { styleThumb, styleThumbFallback } from '../utils/style-thumb.js';

const MODE_KEYS = new Set(['custom', 'reference', 'floorplan', 'cleanup', 'layout']);

export function StyleGallery({ stats, styleKeys, spaceCategory, onPickBuiltin, onPickCustom, onClose, asPage = false, headerExtra = null }) {
  const { t, styleName, lang } = useLocale();
  // "디자인 N개" — 1 이면 단수형 ("1 designs" 방지).
  const usesLabel = (n) => t(n === 1 ? 'communityDesignsOne' : 'communityDesigns', { n });

  // ── 번역 — 온갖 언어의 프롬프트를 설정 언어로. 서버(최저가 모델) 번역 후
  // 문서에 캐시되므로 토글은 부담 없이 켜고 끌 수 있다. 카드마다 따로 —
  // 헤더의 "전체 번역" 은 2.3.0 에서 뺐다 (번역할 게 없는 기본 스타일 랭킹
  // 위에 혼자 떠 있었다).
  const [trMap, setTrMap] = useState({});
  const [cardTr, setCardTr] = useState({}); // 카드 단위 번역 토글
  const [trPending, setTrPending] = useState({}); // 카드 단위 번역 로딩
  const isTr = (s) => cardTr[s.id];
  const shownPrompt = (s) => (isTr(s) && trMap[s.id]) || s.prompt;
  const toggleCardTr = async (e, s) => {
    e.stopPropagation();
    const on = !cardTr[s.id];
    setCardTr((prev) => ({ ...prev, [s.id]: on }));
    if (on && !trMap[s.id]) {
      setTrPending((prev) => ({ ...prev, [s.id]: true }));
      const map = await translateStyles([s.id], lang);
      setTrPending((prev) => ({ ...prev, [s.id]: false }));
      if (map[s.id]) {
        setTrMap((prev) => ({ ...prev, ...map }));
      } else {
        // 실패 — 토글을 되돌려서 "원문 보기" 인데 원문이 보이는 모순 방지
        setCardTr((prev) => ({ ...prev, [s.id]: false }));
      }
    }
  };
  // 상대 시각 — 시드가 몇 시간 전 것이어도 정직하게 보이도록.
  const timeAgo = (item) => {
    const ms = Date.now() - (item.lastUsedAt?.toMillis?.() || 0);
    const m = Math.floor(ms / 60000);
    if (m < 1) return t('timeJustNow');
    if (m < 60) return t('timeAgoMin', { n: m });
    return t('timeAgoHour', { n: Math.floor(m / 60) });
  };

  // 카드: 본문 탭 = 스타일 사용(원문 프롬프트 — 같은 스타일로 집계되도록),
  // 우하단 미니 버튼 = 이 카드만 번역/원문 토글.
  const CustomCard = ({ s, live, meta }) => (
    <div
      role="button"
      tabIndex={0}
      className={`gallery-custom-card${live ? ' gallery-live-card' : ''}`}
      onClick={() => onPickCustom(s.prompt)}
      onKeyDown={(e) => { if (e.key === 'Enter') onPickCustom(s.prompt); }}
    >
      <p className="gallery-custom-prompt">{shownPrompt(s)}</p>
      <span className="gallery-custom-foot2">
        {meta}
        <button type="button" className="gallery-card-tr" disabled={trPending[s.id]} onClick={(e) => toggleCardTr(e, s)}>
          {trPending[s.id] ? t('galleryTranslating') : isTr(s) ? t('galleryOriginal') : t('galleryTranslate')}
        </button>
      </span>
    </div>
  );
  // ── 둘러보기 모드: [섞기 | 실시간] 토글.
  // 섞기 = 랜덤 덱 8개 (다시 누르면 재셔플). 실시간 = 누른 순간부터 새로
  // 사용되는 커스텀만 최대 8개, 새 것이 오면 아래로 밀림 (1.2초당 1장).
  const liveCat = spaceCategory || 'interior';
  const [mode, setMode] = useState('shuffle');
  const modeRef = useRef('shuffle');
  const [liveItems, setLiveItems] = useState([]);
  const [, tick] = useState(0); // 상대 시각 갱신용 60초 틱
  useEffect(() => {
    const iv = setInterval(() => tick((x) => x + 1), 60 * 1000);
    return () => clearInterval(iv);
  }, []);
  const queueRef = useRef([]);
  const seenRef = useRef(new Set());
  const snapRef = useRef([]); // 최신 스냅샷 — live 진입 시 "최근 30분" 시드용
  const passes = (s) => s.type === 'custom' && s.public !== false && s.prompt
    && (s.spaceCategory || 'interior') === liveCat;
  const stampOf = (s) => `${s.id}_${s.lastUsedAt?.toMillis?.() || 0}`;
  useEffect(() => {
    let first = true;
    const q = query(collection(db, 'styleStats'), orderBy('lastUsedAt', 'desc'), limit(15));
    const unsub = onSnapshot(q, (snap) => {
      snapRef.current = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      if (first) {
        // 첫 스냅샷 = 기준선 — 표시하지 않고 "본 것" 으로만 기록.
        // 실시간 모드는 진입 이후의 새 사용만 보여준다.
        first = false;
        snap.docs.forEach((doc) => {
          seenRef.current.add(stampOf({ id: doc.id, ...doc.data() }));
        });
        return;
      }
      snap.docChanges().forEach((ch) => {
        if (ch.type === 'removed') return;
        const s = { id: ch.doc.id, ...ch.doc.data() };
        if (!passes(s)) return;
        const stamp = stampOf(s);
        if (seenRef.current.has(stamp)) return;
        seenRef.current.add(stamp);
        if (modeRef.current === 'live') queueRef.current.push(s);
      });
    }, () => {});
    const iv = setInterval(() => {
      if (modeRef.current !== 'live') return;
      const next = queueRef.current.shift();
      if (!next) return;
      setLiveItems((prev) => [
        { ...next, _k: `${next.id}_${Date.now()}` },
        ...prev.filter((p) => p.id !== next.id),
      ].slice(0, 8));
    }, 1200);
    return () => { unsub(); clearInterval(iv); };
  }, [liveCat]);
  const enterLive = () => {
    queueRef.current = [];
    // 시드: 최근 24시간 사용분 (구독 창이 "최근 15개 문서"라 볼륨이 커지면
    // 자연히 몇 분 단위로 좁아진다). 지금 볼륨에선 30분이 너무 짧아 Live 가
    // 자주 비어 보여서 넉넉하게 — 진입 후 새 사용은 그 위로 실시간 추가.
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    const seed = snapRef.current
      .filter((s) => passes(s) && (s.lastUsedAt?.toMillis?.() || 0) >= cutoff)
      .slice(0, 8)
      .map((s) => ({ ...s, _k: s.id, _live: false }));
    setLiveItems(seed);
    modeRef.current = 'live';
    setMode('live');
  };
  const enterShuffle = () => {
    modeRef.current = 'shuffle';
    setMode('shuffle');
    reshuffle(); // 이미 섞기 모드여도 다시 누르면 재셔플
  };

  // ── "둘러보기" 랜덤 덱 — 모든 공개 커스텀이 공평하게 재발견될 기회.
  // 무작위 5개.
  const [deck, setDeck] = useState([]);
  const reshuffle = () => {
    if (!stats) return;
    const base = stats.customs.filter(
      (s) => (s.spaceCategory || 'interior') === liveCat,
    );
    const arr = [...base];
    for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    setDeck(arr.slice(0, 5));
  };
  useEffect(reshuffle, [stats, liveCat]);

  if (!stats) return null;


  // 현재 공간 카테고리로 한정 — 인테리어 커스텀이 익스테리어 화면에 안 섞이게.
  const cat = spaceCategory || 'interior';
  const builtins = stats.builtins
    .filter((s) => {
      const bare = s.id.replace(/^builtin_/, '');
      return styleKeys.includes(bare) && !MODE_KEYS.has(bare)
        && (s.spaceCategory || 'interior') === cat && rankUses(s) > 0;
    })
    .slice(0, 12);
  const customs = stats.customs
    .filter((s) => (s.spaceCategory || 'interior') === cat && rankUses(s) > 0)
    .slice(0, 30);

  // 랭킹에는 "커스텀 스타일" 집계가 한 항목으로 선다 — 인라인 칩과 동일 기준.
  // 개별 프롬프트는 아래 섹션에. 카드를 누르면 그 섹션으로 스크롤.
  const customAgg = customs.length > 0
    ? { id: '__customAgg__', monthlyUses: customAggregate(stats, cat, customs) }
    : null;
  const ranking = [...builtins, ...(customAgg ? [customAgg] : [])]
    .sort((a, b) => rankUses(b) - rankUses(a))
    .slice(0, 12);
  const scrollToCustoms = () => {
    document.querySelector('.gallery-customs-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      className={asPage ? 'gallery-page' : 'gallery-overlay'}
      role={asPage ? undefined : 'dialog'}
      aria-modal={asPage ? undefined : 'true'}
      aria-label={t('galleryTitle')}
    >
      <header className="gallery-header">
        <h2>{t('galleryTitle')}</h2>
        {!asPage && (
          <span className="gallery-header-actions">
            <button type="button" className="gallery-close" onClick={onClose} aria-label="close">
              <i className="material-symbols-rounded">close</i>
            </button>
          </span>
        )}
      </header>
      {headerExtra}
      <div className="gallery-body">
        {asPage && ranking.length === 0 && customs.length === 0 && (
          <p className="gallery-live-waiting">{t('galleryEmpty')}</p>
        )}
        {ranking.length > 0 && (
          <section>
            <h3 className="gallery-section-title">{t('galleryBuiltinTitle')}</h3>
            <div className="gallery-style-grid">
              {ranking.map((s, i) => {
                if (s.id === '__customAgg__') {
                  return (
                    <button key={s.id} type="button" className="gallery-style-card" onClick={scrollToCustoms}>
                      <span className="gallery-style-thumb gallery-style-thumb-custom">
                        <i className="material-symbols-rounded">edit</i>
                        <span className="gallery-style-rank">{i + 1}</span>
                      </span>
                      <span className="gallery-style-name">{t('customStylesChip')}</span>
                      <span className="gallery-style-users">{usesLabel(rankUses(s))}</span>
                    </button>
                  );
                }
                const bare = s.id.replace(/^builtin_/, '');
                return (
                  <button key={s.id} type="button" className="gallery-style-card" onClick={() => onPickBuiltin(bare)}>
                    <span className="gallery-style-thumb">
                      <img src={styleThumb(spaceCategory, bare)} alt="" loading="lazy" onError={styleThumbFallback(spaceCategory, bare)} />
                      <span className="gallery-style-rank">{i + 1}</span>
                    </span>
                    <span className="gallery-style-name">{styleName(bare, spaceCategory)}</span>
                    <span className="gallery-style-users">{usesLabel(rankUses(s))}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
        {/* "요즘 인기 커스텀" 섹션은 뺐다 (2026-10-23) — 커스텀 355개 중 서로 다른 2명이 같은 문구를 쓴 건
            "Kitchen"·"luxury" 둘뿐이라 늘 비거나, 사용 횟수 기준이면 한 사람의 반복이 "인기" 로 떴다. */}
        <section className="gallery-customs-section">
          <div className="gallery-discover-head">
            <h3 className="gallery-section-title">
              {t('galleryDiscoverTitle')}
              {mode === 'live' && <span className="gallery-live-dot" aria-hidden="true" />}
            </h3>
            <div className="gallery-mode-toggle" role="tablist">
              <button
                type="button"
                className={mode === 'shuffle' ? 'selected' : ''}
                onClick={enterShuffle}
              >
                {t('galleryShuffle')}
              </button>
              <button
                type="button"
                className={mode === 'live' ? 'selected' : ''}
                onClick={enterLive}
              >
                {t('galleryLiveTab')}
              </button>
            </div>
          </div>
          <div className="gallery-custom-list">
            {mode === 'shuffle' && deck.map((s) => (
              <CustomCard key={s.id} s={s}
                meta={<span className="gallery-custom-meta">{rankUses(s) >= 2 ? usesLabel(rankUses(s)) : ''}</span>} />
            ))}
            {mode === 'live' && liveItems.map((s) => (
              <CustomCard key={s._k} s={s} live={!!s._live}
                meta={<span className="gallery-custom-meta">{timeAgo(s)}</span>} />
            ))}
            {mode === 'live' && liveItems.length === 0 && (
              <p className="gallery-live-waiting">{t('galleryLiveWaiting')}</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
